import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';
import ModeToggle from '../components/ModeToggle';
import FRQQuestion from '../components/FRQQuestion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookletTakePage = ({ isPractice = false }) => {
  const { bookletId, taskId } = useParams();
  const id = isPractice ? taskId : bookletId;
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { mode, config, isAP, isTYT } = useMode();
  const navigate = useNavigate();
  
  const [booklet, setBooklet] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  useEffect(() => {
    fetchBookletDetails();
  }, [id, isPractice]);

  const fetchBookletDetails = async () => {
    setLoading(true);
    try {
      const endpoint = isPractice 
        ? `${API}/questions/practice/${id}`
        : `${API}/questions/booklets/${id}`;
      
      const response = await axios.get(endpoint, {
        withCredentials: true
      });
      
      if (isPractice) {
        setBooklet(response.data.task);
        setQuestions(response.data.questions || []);
      } else {
        setBooklet(response.data.booklet);
        setQuestions(response.data.questions || []);
      }
    } catch (error) {
      console.error('Error fetching booklet:', error);
      alert(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length === 0) {
      alert(t('please_answer_questions'));
      return;
    }

    const confirmSubmit = window.confirm(t('confirm_submit_booklet'));
    if (!confirmSubmit) return;

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/questions/booklets/${bookletId}/submit`,
        { booklet_id: bookletId, answers },
        { withCredentials: true }
      );
      
      // Navigate to results page with submission ID
      navigate(`/booklet/${bookletId}/results/${response.data.submission_id}`);
    } catch (error) {
      console.error('Error submitting booklet:', error);
      alert(t('error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (!booklet || questions.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <p className="text-xl text-gray-600">{t('no_questions_in_booklet')}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 text-blue-600 hover:underline"
          >
            {t('go_back')}
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <ModeToggle />
      <Navbar title={booklet.title} />

      <div className="max-w-4xl mx-auto px-4 py-8">
        
        {/* Mode Banner */}
        <div className={`mb-4 p-4 rounded-lg ${isAP ? 'bg-blue-100 border-l-4 border-blue-600' : 'bg-red-100 border-l-4 border-red-600'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-2xl">{config.icon}</span>
              <div>
                <div className="font-bold text-gray-800">{config.name}</div>
                <div className="text-sm text-gray-600">
                  {isAP ? 'Deep understanding with justification' : 'Hızlı çözüm ve test stratejisi'}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-800">{config.timePerQuestion}s</div>
              <div className="text-xs text-gray-600">per question</div>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>{t('question')} {currentQuestionIndex + 1} {t('of')} {questions.length}</span>
            <span>{Object.keys(answers).length} {t('answered')}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all duration-300 ${isAP ? 'bg-blue-600' : 'bg-red-600'}`}
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6">
          <div className="mb-4">
            <span className="inline-block bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              {currentQuestion.subject} - {currentQuestion.difficulty}
            </span>
          </div>

          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              {language === 'TR' ? currentQuestion.stem_TR : (currentQuestion.stem_EN || currentQuestion.stem_TR)}
            </h2>
          </div>

          {/* Answer Input - Mode Aware */}
          {isAP && currentQuestion.question_type === 'FRQ' ? (
            // AP Mode: FRQ with CER format
            <FRQQuestion
              question={currentQuestion}
              onAnswer={(answer) => handleAnswerChange(currentQuestion.id, answer)}
              currentAnswer={answers[currentQuestion.id]}
            />
          ) : currentQuestion.question_type === 'MCQ' && currentQuestion.options && currentQuestion.options.length > 0 ? (
            // MCQ for both modes
            <>
              {isTYT && (
                <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-500 rounded">
                  <p className="text-sm text-yellow-800">
                    💡 <strong>Test Taktiği:</strong> Önce kesin yanlışları elemeyi dene. {config.timePerQuestion} saniye içinde çöz!
                  </p>
                </div>
              )}
              <div className="space-y-3">
                {currentQuestion.options.map((option, idx) => (
                  <label
                    key={idx}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      answers[currentQuestion.id] === option.charAt(0)
                        ? (isAP ? 'border-blue-600 bg-blue-50' : 'border-red-600 bg-red-50')
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestion.id}`}
                      value={option.charAt(0)}
                      checked={answers[currentQuestion.id] === option.charAt(0)}
                      onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                      className={`w-5 h-5 mr-4 ${isAP ? 'text-blue-600' : 'text-red-600'}`}
                    />
                    <span className="text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </>
          ) : (
            // Fallback: Text input
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('your_answer')}:
              </label>
              <textarea
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                placeholder={t('type_your_answer')}
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
            disabled={currentQuestionIndex === 0}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            ← {t('previous')}
          </button>

          <div className="text-center">
            <div className="flex space-x-2">
              {questions.map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentQuestionIndex(idx)}
                  className={`w-10 h-10 rounded-lg font-semibold transition-all ${
                    idx === currentQuestionIndex
                      ? 'bg-blue-600 text-white'
                      : answers[q.id]
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          </div>

          {currentQuestionIndex === questions.length - 1 ? (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {submitting ? t('submitting') : t('submit_booklet')} ✓
            </button>
          ) : (
            <button
              onClick={() => setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1))}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              {t('next')} →
            </button>
          )}
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-600 hover:text-gray-800 underline"
          >
            {t('cancel_and_go_back')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookletTakePage;
